import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Importing specific icons from React Icons
import { 
  FaFacebookF, 
  FaInstagram, 
  FaLinkedinIn, 
  FaWhatsapp,
  FaRegClock,
} from 'react-icons/fa6';
import { 
  HiOutlineMapPin, 
  HiOutlinePhone, 
  HiOutlinePaperAirplane 
} from 'react-icons/hi2';
import { BiLoaderAlt } from 'react-icons/bi';

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const Contact = () => {
  const [status, setStatus] = useState('idle'); // idle, sending, success

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    setTimeout(() => setStatus('success'), 1500);
  };

  return (
    <motion.section
      className="py-24 bg-white scroll-mt-28"
      id="contact"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={fadeUp}
    >
      <div className="max-w-7xl mx-auto px-4">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* LEFT SIDE */}
          <motion.div
            className="space-y-12"
            variants={stagger}
          >

            <motion.div variants={item}>
              <span className="text-[#3d60ad] font-bold tracking-widest uppercase text-sm mb-3 block">
                Get In Touch
              </span>

              <h2 className="text-4xl md:text-5xl font-black text-[#3d60ad] mb-6 leading-tight">
                Have questions? <br />
                <span className="text-slate-400">We're here to help.</span>
              </h2>

              <p className="text-slate-500 text-lg max-w-md">
                Whether you're looking for treatment or want to support our foundation's mission, our team is ready to assist you.
              </p>
            </motion.div>

            <motion.div variants={stagger} className="grid gap-8">

              {/* Item 1 */}
              <motion.div variants={item} whileHover={{ x: 6 }} className="flex gap-6 group">
                <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center shrink-0 text-[#3d60ad] group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <HiOutlineMapPin size={28} />
                </div>
                <div>
                  <h4 className="font-bold text-[#3d60ad] mb-1">Clinic Location</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    123 Dental Plaza, Healthcare Avenue,<br />
                    Hyderabad, Telangana 500001
                  </p>
                </div>
              </motion.div>

              {/* Item 2 */}
              <motion.div variants={item} whileHover={{ x: 6 }} className="flex gap-6 group">
                <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center shrink-0 text-[#3d60ad] group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <HiOutlinePhone size={28} />
                </div>
                <div>
                  <h4 className="font-bold text-[#3d60ad] mb-1">Call Us</h4>
                  <p className="text-slate-500 text-sm font-medium">+91 94909 48081</p>
                  <p className="text-slate-500 text-sm">+91 95420 27700</p>
                </div>
              </motion.div>

              {/* Item 3 */}
{/*               <motion.div variants={item} whileHover={{ x: 6 }} className="flex gap-6 group">
                <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center shrink-0 text-[#3d60ad] group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <FaRegClock size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-[#3d60ad] mb-1">Working Hours</h4>
                  <p className="text-slate-500 text-sm">Mon - Sat: 9:00 AM - 8:00 PM</p>
                  <p className="text-[#3d60ad] text-[11px] font-black mt-1 uppercase tracking-wider">
                    Sunday: Emergency Only
                  </p>
                </div>
              </motion.div> */}

            </motion.div>

            {/* SOCIAL */}
            <motion.div variants={item} className="pt-8 border-t border-slate-100">
              <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                Stay Connected
              </p>

              <div className="flex gap-3">
                {[
                  FaFacebookF,
                  FaInstagram,
                  FaLinkedinIn,
                  FaWhatsapp
                ].map((Icon, i) => (
                  <motion.a
                    key={i}
                    href="#"
                    whileHover={{ scale: 1.1, y: -3 }}
                    className="w-12 h-12 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-primary hover:text-white hover:border-[#3d60ad] transition-all duration-300"
                  >
                    <Icon size={18} />
                  </motion.a>
                ))}
              </div>
            </motion.div>

          </motion.div>

          {/* RIGHT FORM */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >

            <div className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-2xl shadow-slate-200/60 border border-slate-100">

              {status === 'success' ? (
                <div className="text-center py-12">
                  <motion.div
                    className="w-20 h-20 bg-teal-100 text-[#3d60ad] rounded-full flex items-center justify-center mx-auto mb-6"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <HiOutlinePaperAirplane size={32} className="rotate-45" />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-[#3d60ad] mb-2">
                    Message Sent!
                  </h3>

                  <p className="text-slate-500 mb-8">
                    Thank you for reaching out. Our team will contact you shortly.
                  </p>

                  <button
                    onClick={() => setStatus('idle')}
                    className="text-[#3d60ad] font-bold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <motion.form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    <input className="w-full bg-slate-50 p-4 rounded-2xl" placeholder="Your Name" />
                    <input className="w-full bg-slate-50 p-4 rounded-2xl" placeholder="Email" />

                  </div>

                  <select className="w-full bg-slate-50 p-4 rounded-2xl">
                    <option>General Inquiry</option>
                    <option>Appointment</option>
                    <option>Donation</option>
                  </select>

                  <textarea
                    rows="4"
                    className="w-full bg-slate-50 p-4 rounded-2xl"
                    placeholder="Message"
                  />

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={status === 'sending'}
                    className="w-full bg-primary text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3"
                  >
                    {status === 'sending' ? (
                      <BiLoaderAlt className="animate-spin" size={20} />
                    ) : (
                      <>
                        Send Message
                        <HiOutlinePaperAirplane size={18} />
                      </>
                    )}
                  </motion.button>

                </motion.form>
              )}

            </div>

          </motion.div>

        </div>
      </div>
    </motion.section>
  );
};

export default Contact;